import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useWorkspace } from '../lib/WorkspaceContext'
import { notificarTurno } from '../lib/notificarTurno'
import './Turnos.css'

const ESTADO_LABEL = {
  pendiente: 'Pendiente', confirmado: 'Confirmado', cancelado: 'Cancelado',
  reprogramado: 'Reprogramado', completado: 'Completado', no_show: 'No asististe'
}

function isoFecha(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function fmtGCal(iso) {
  return new Date(iso).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')
}

function linkGoogleCalendar({ titulo, inicio, fin, detalle, ubicacion }) {
  const p = new URLSearchParams({
    action: 'TEMPLATE', text: titulo,
    dates: `${fmtGCal(inicio)}/${fmtGCal(fin)}`,
    details: detalle || '', location: ubicacion || ''
  })
  return `https://calendar.google.com/calendar/render?${p.toString()}`
}

const MODALIDAD_LABEL = { presencial: 'Presencial', virtual: 'Por videollamada', ambas: 'Presencial o virtual' }

function traducirError(msg = '') {
  if (msg.includes('slot_no_disponible')) return 'Ese horario ya no está disponible. Elegí otro.'
  if (msg.includes('fuera_de_ventana_cancelacion')) return 'Ya pasó el plazo para cancelar este turno. Escribile a tu nutri.'
  if (msg.includes('fuera_de_ventana_reprogramacion')) return 'Ya pasó el plazo para reprogramar este turno. Escribile a tu nutri.'
  if (msg.includes('max_reprogramaciones_alcanzado')) return 'Ya reprogramaste este turno el máximo de veces permitido.'
  if (msg.includes('turnero_inactivo')) return 'El turnero no está disponible en este momento.'
  if (msg.includes('no_sos_miembro')) return 'No podés reservar en este espacio.'
  return 'No se pudo completar. Probá de nuevo.'
}

export default function Turnos({ usuario, onVolver }) {
  const { workspaceActivo } = useWorkspace()
  const workspaceId = workspaceActivo?.id

  const [cfg, setCfg] = useState(null)
  const [tipos, setTipos] = useState([])
  const [turnos, setTurnos] = useState([])
  const [cargando, setCargando] = useState(true)

  const [modo, setModo] = useState('lista')       // 'lista' | 'reservar'
  const [tipoSel, setTipoSel] = useState(null)
  const [modalidadSel, setModalidadSel] = useState(null)  // 'presencial' | 'virtual', resuelta
  const [slots, setSlots] = useState([])
  const [cargandoSlots, setCargandoSlots] = useState(false)
  const [reservando, setReservando] = useState(false)
  const [exito, setExito] = useState(null)        // turno recién reservado (para pantalla de confirmación)

  const [reprogTurno, setReprogTurno] = useState(null)
  const [reprogSlots, setReprogSlots] = useState([])
  // request-id: una recarga vieja en vuelo no debe pisar a una más nueva.
  const cargaRef = useRef(0)

  const zona = cfg?.zona_horaria || 'America/Argentina/Buenos_Aires'
  const fmt = (iso, opts) => new Intl.DateTimeFormat('es-AR', { timeZone: zona, ...opts }).format(new Date(iso))

  const cargar = useCallback(async () => {
    if (!workspaceId) return
    const cargaId = ++cargaRef.current
    setCargando(true)
    const [{ data: c }, { data: tp }, { data: tn }] = await Promise.all([
      supabase.from('configuracion_turnero').select('*').eq('workspace_id', workspaceId).maybeSingle(),
      supabase.from('tipos_consulta').select('*').eq('workspace_id', workspaceId).eq('activo', true).order('orden').order('created_at'),
      supabase.from('turnos')
        .select('id, inicio, fin, estado, modalidad, link_videollamada, cantidad_reprogramaciones, motivo_cancelacion, tipos_consulta(nombre, modalidad)')
        .eq('workspace_id', workspaceId).eq('paciente_id', usuario.id).order('inicio', { ascending: false })
    ])
    if (cargaId !== cargaRef.current) return
    setCfg(c || null)
    setTipos(tp || [])
    setTurnos(tn || [])
    setCargando(false)
  }, [workspaceId, usuario.id])

  useEffect(() => { cargar() }, [cargar])

  const pedirSlots = async (tipo, setFn, setLoad, excluirTurnoId, modalidad) => {
    setLoad(true)
    const hoy = new Date()
    const fin = new Date(); fin.setDate(fin.getDate() + 30)
    const params = {
      p_workspace_id: workspaceId, p_tipo_consulta_id: tipo.id,
      p_desde: isoFecha(hoy), p_hasta: isoFecha(fin)
    }
    if (excluirTurnoId) params.p_excluir_turno_id = excluirTurnoId
    if (modalidad) params.p_modalidad = modalidad
    const { data, error } = await supabase.rpc('slots_disponibles', params)
    setLoad(false)
    if (error) { alert(traducirError(error.message)); setFn([]); return }
    setFn(data || [])
  }

  // Al elegir un tipo: si es fijo, resolvemos la modalidad y vamos directo
  // a horarios. Si es 'ambas', se pregunta antes de mostrar horarios.
  const elegirTipo = (tipo) => {
    setTipoSel(tipo)
    if (tipo.modalidad === 'ambas') {
      setModalidadSel(null)
    } else {
      setModalidadSel(tipo.modalidad)
      pedirSlots(tipo, setSlots, setCargandoSlots, null, tipo.modalidad)
    }
  }
  const elegirModalidad = (m) => {
    setModalidadSel(m)
    pedirSlots(tipoSel, setSlots, setCargandoSlots, null, m)
  }
  const resetReserva = () => { setTipoSel(null); setModalidadSel(null); setSlots([]) }

  const reservar = async (slot) => {
    setReservando(true)
    const params = { p_workspace_id: workspaceId, p_tipo_consulta_id: tipoSel.id, p_inicio: slot.inicio }
    if (modalidadSel) params.p_modalidad = modalidadSel
    const { data, error } = await supabase.rpc('reservar_turno', params)
    setReservando(false)
    if (error) { alert(traducirError(error.message)); pedirSlots(tipoSel, setSlots, setCargandoSlots, null, modalidadSel); return }
    notificarTurno(data.id, 'confirmacion')
    setExito({ ...data, tipo: tipoSel })
    await cargar()
  }

  // La RPC devuelve la fila de turnos ya actualizada: se mergea al estado
  // local en el acto (preservando el join embebido de tipos_consulta) para
  // que "Próximos" / "Historial" se recalculen sin esperar al refetch.
  const mergeTurno = (row) => {
    if (!row?.id) return
    setTurnos(prev => prev.map(x => x.id === row.id ? { ...x, ...row } : x))
  }

  const cancelar = async (t) => {
    if (!window.confirm('¿Seguro que querés cancelar este turno?')) return
    const { data, error } = await supabase.rpc('cancelar_turno', { p_turno_id: t.id, p_motivo: null })
    if (error) { alert(traducirError(error.message)); return }
    mergeTurno(data)
    notificarTurno(t.id, 'cancelacion')
    await cargar()
  }

  // slots_disponibles necesita el id del tipo de consulta del turno. No lo
  // tenemos embebido (el select trae solo nombre/modalidad), así que lo
  // pedimos con una consulta liviana.
  const abrirReprog = async (t) => {
    setReprogTurno(t)
    setReprogSlots([])
    const { data } = await supabase.from('turnos').select('tipo_consulta_id').eq('id', t.id).single()
    if (!data?.tipo_consulta_id) return
    // el turno ya tiene su modalidad resuelta y persistida: no se re-pregunta
    pedirSlots({ id: data.tipo_consulta_id }, setReprogSlots, () => {}, t.id, t.modalidad)
  }

  const confirmarReprog = async (slot) => {
    const { data, error } = await supabase.rpc('reprogramar_turno', { p_turno_id: reprogTurno.id, p_nuevo_inicio: slot.inicio })
    if (error) { alert(traducirError(error.message)); return }
    mergeTurno(data)
    notificarTurno(reprogTurno.id, 'reprogramacion')
    setReprogTurno(null)
    await cargar()
  }

  const ahora = Date.now()
  const proximos = turnos.filter(t => ['pendiente', 'confirmado'].includes(t.estado) && new Date(t.inicio).getTime() >= ahora)
    .sort((a, b) => new Date(a.inicio) - new Date(b.inicio))
  const historial = turnos.filter(t => !proximos.includes(t))

  /* ---------- pantalla de confirmación ---------- */
  if (exito) {
    const esPresencial = exito.modalidad === 'presencial'
    const gcal = linkGoogleCalendar({
      titulo: `${exito.tipo.nombre} — ${workspaceActivo.nombre}`,
      inicio: exito.inicio, fin: exito.fin,
      detalle: '', ubicacion: esPresencial ? (cfg?.direccion_presencial || workspaceActivo.nombre) : 'Videollamada'
    })
    return (
      <div className="turnos-page">
        <div className="page-header"><h1 className="page-titulo">¡Turno agendado!</h1></div>
        <div className="turnos-exito">
          <p className="turnos-exito-fecha">{fmt(exito.inicio, { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}</p>
          <p className="turnos-exito-tipo">{exito.tipo.nombre} · {MODALIDAD_LABEL[exito.modalidad]}</p>
          {esPresencial && cfg?.direccion_presencial && <p className="turnos-exito-tipo">📍 {cfg.direccion_presencial}</p>}
          {cfg?.mensaje_cuerpo && <p className="turnos-exito-msg">{cfg.mensaje_cuerpo}</p>}
          <a className="btn-primario turnos-gcal" href={gcal} target="_blank" rel="noopener noreferrer">Agregar a Google Calendar</a>
          <p className="turnos-exito-hint">Te mandamos los detalles por mail.</p>
          <button className="btn-secundario" onClick={() => { setExito(null); setModo('lista'); resetReserva() }}>Ver mis turnos</button>
        </div>
      </div>
    )
  }

  /* ---------- flujo de reserva ---------- */
  if (modo === 'reservar') {
    const porDia = {}
    for (const s of slots) {
      const k = fmt(s.inicio, { weekday: 'long', day: 'numeric', month: 'long' })
      ;(porDia[k] = porDia[k] || []).push(s)
    }
    return (
      <div className="turnos-page">
        <div className="page-header">
          <button className="turnos-back" onClick={() => { setModo('lista'); resetReserva() }}>← Mis turnos</button>
          <h1 className="page-titulo">Reservar turno</h1>
        </div>

        {!tipoSel ? (
          <div className="turnos-tipos">
            <p className="turnos-label">Elegí el tipo de consulta</p>
            {tipos.map(t => (
              <button key={t.id} className="turnos-tipo-card" onClick={() => elegirTipo(t)}>
                <strong>{t.nombre}</strong>
                <span>{t.duracion_min} min · {MODALIDAD_LABEL[t.modalidad]}</span>
              </button>
            ))}
          </div>
        ) : (tipoSel.modalidad === 'ambas' && !modalidadSel) ? (
          <div className="turnos-tipos">
            <button className="turnos-cambiar-tipo" onClick={resetReserva}>{tipoSel.nombre} — cambiar</button>
            <p className="turnos-label">¿Cómo preferís esta consulta?</p>
            <button className="turnos-tipo-card" onClick={() => elegirModalidad('presencial')}>
              <strong>Presencial</strong>
              {cfg?.direccion_presencial && <span>📍 {cfg.direccion_presencial}</span>}
            </button>
            <button className="turnos-tipo-card" onClick={() => elegirModalidad('virtual')}>
              <strong>Por videollamada</strong>
            </button>
          </div>
        ) : (
          <>
            <button className="turnos-cambiar-tipo" onClick={resetReserva}>
              {tipoSel.nombre} · {MODALIDAD_LABEL[modalidadSel]} — cambiar
            </button>
            {modalidadSel === 'presencial' && cfg?.direccion_presencial && (
              <p className="turnos-label">📍 {cfg.direccion_presencial}</p>
            )}
            {cargandoSlots ? (
              <p className="turnos-vacio">Buscando horarios...</p>
            ) : slots.length === 0 ? (
              <p className="turnos-vacio">No hay horarios disponibles en los próximos 30 días.</p>
            ) : (
              Object.entries(porDia).map(([dia, lista]) => (
                <div key={dia} className="turnos-dia">
                  <p className="turnos-dia-titulo">{dia}</p>
                  <div className="turnos-slots">
                    {lista.map(s => (
                      <button key={s.inicio} className="turnos-slot" disabled={reservando} onClick={() => reservar(s)}>
                        {fmt(s.inicio, { hour: '2-digit', minute: '2-digit' })}
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>
    )
  }

  /* ---------- lista ---------- */
  const turneroOff = !cfg?.turnero_activo || tipos.length === 0

  return (
    <div className="turnos-page">
      <div className="page-header">
        {onVolver && <button className="turnos-back" onClick={onVolver}>← Inicio</button>}
        <h1 className="page-titulo">Mis turnos</h1>
      </div>

      {cargando ? (
        <p className="turnos-vacio">Cargando...</p>
      ) : (
        <>
          {!turneroOff && (
            <button className="btn-primario turnos-reservar-btn" onClick={() => setModo('reservar')}>+ Reservar un turno</button>
          )}
          {turneroOff && <p className="turnos-vacio">Tu nutri todavía no habilitó la reserva de turnos.</p>}

          {proximos.length > 0 && (
            <div className="turnos-seccion">
              <p className="turnos-label">Próximos</p>
              {proximos.map(t => (
                <div key={t.id} className="turnos-card">
                  <p className="turnos-card-fecha">{fmt(t.inicio, { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}</p>
                  <p className="turnos-card-meta">{t.tipos_consulta?.nombre} · {MODALIDAD_LABEL[t.modalidad]}</p>
                  {t.modalidad === 'presencial' && cfg?.direccion_presencial && (
                    <p className="turnos-card-meta">📍 {cfg.direccion_presencial}</p>
                  )}
                  {t.modalidad === 'virtual' && t.link_videollamada && (
                    <a className="turnos-card-link" href={t.link_videollamada} target="_blank" rel="noopener noreferrer">Entrar a la videollamada</a>
                  )}
                  {reprogTurno?.id === t.id ? (
                    <div className="turnos-reprog">
                      <p className="turnos-label">Elegí el nuevo horario</p>
                      {reprogSlots.length === 0 ? <p className="turnos-vacio">Sin horarios disponibles.</p> : (
                        <div className="turnos-slots">
                          {reprogSlots.slice(0, 24).map(s => (
                            <button key={s.inicio} className="turnos-slot" onClick={() => confirmarReprog(s)}>
                              {fmt(s.inicio, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </button>
                          ))}
                        </div>
                      )}
                      <button className="turnos-link" onClick={() => setReprogTurno(null)}>Cerrar</button>
                    </div>
                  ) : (
                    <div className="turnos-card-acciones">
                      <button onClick={() => abrirReprog(t)}>Reprogramar</button>
                      <button className="turnos-btn-danger" onClick={() => cancelar(t)}>Cancelar</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {historial.length > 0 && (
            <div className="turnos-seccion">
              <p className="turnos-label">Historial</p>
              {historial.map(t => (
                <div key={t.id} className={`turnos-card turnos-card-${t.estado}`}>
                  <p className="turnos-card-fecha">{fmt(t.inicio, { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                  <p className="turnos-card-meta">
                    {t.tipos_consulta?.nombre} · {MODALIDAD_LABEL[t.modalidad]} · {ESTADO_LABEL[t.estado]}
                    {t.estado === 'cancelado' && t.motivo_cancelacion ? ` · ${t.motivo_cancelacion}` : ''}
                  </p>
                </div>
              ))}
            </div>
          )}

          {!turneroOff && proximos.length === 0 && historial.length === 0 && (
            <p className="turnos-vacio">Todavía no tenés turnos.</p>
          )}
        </>
      )}
    </div>
  )
}
